import { Evaluatie, usePutEvaluatie } from "@/entities/evaluatie";
import { Button, ErrorDisplay, Form, Separated } from "@/shared/components";
import { useArchiveVisibility } from "@/shared/hooks";
import { useDebouncedCallback } from "@/shared/hooks/use-debounced-callback";
import { optionalFormString } from "@/shared/utils/zod-helpers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { FieldPath, useForm } from "react-hook-form";
import z from "zod";
import { buildEvaluatiePayload } from "../build-evaluatie-payload";
import { EvaluatiePunt } from "./evaluatie-punt";
import { EvaluatieSectie } from "./evaluatie-sectie";

const schema = z.object({
    busInzet: z.object({
        deWit: optionalFormString(),
        transvision: optionalFormString(),
    }),
});

type BusinzetEvaluatieFormData = z.input<typeof schema>;

type BusinzetEvaluatieProps = {
    dossierId: string;
    evaluatie: Evaluatie | null;
};

export function BusinzetEvaluatie({ dossierId, evaluatie }: BusinzetEvaluatieProps) {
    const [validationError, setValidationError] = useState<Error | null>(null);
    const isArchived = useArchiveVisibility();
    const form = useForm<BusinzetEvaluatieFormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            busInzet: {
                deWit: evaluatie?.busInzet?.deWit ?? "",
                transvision: evaluatie?.busInzet?.transvision ?? "",
            },
        },
    });
    const putEvaluatie = usePutEvaluatie();

    const handleChange = () => {
        // parse via schema to trimmed values. With the current schema, validation should always succeed.
        const parsed = schema.safeParse(form.getValues());

        if (!parsed.success) {
            setValidationError(new Error("Validatiefout"));
            return;
        }

        const { busInzet } = parsed.data;

        const nextEvaluatie = buildEvaluatiePayload(evaluatie, {
            busInzet,
        });

        putEvaluatie.mutate({ dossierId, ...nextEvaluatie });
    };

    const handleCopy = (name: FieldPath<BusinzetEvaluatieFormData>) => {
        const alternatiefVervoer = evaluatie?.evaluatiePunten?.alternatiefVervoer;
        if (alternatiefVervoer) {
            form.setValue(name, alternatiefVervoer);
            handleChange();
        }
    };

    const debouncedHandleChange = useDebouncedCallback(handleChange);

    return (
        <Form {...form}>
            <form onChange={isArchived ? undefined : debouncedHandleChange}>
                <fieldset disabled={isArchived}>
                    <Separated>
                        <EvaluatieSectie
                            title="Evaluatie externe businzet"
                            description="Deze informatie wordt gedeeld met de busleverancier(s)."
                            headingLevel="h2"
                        >
                            <div className="grid gap-2">
                                <EvaluatiePunt
                                    form={form}
                                    name="busInzet.transvision"
                                    label="Transvision"
                                    placeholder="Beschrijf de businzet"
                                    defaultOpen={!!evaluatie?.busInzet?.transvision}
                                    onClear={handleChange}
                                    readonly={isArchived}
                                    action={
                                        !isArchived && (
                                            <KopieerAlternatiefVervoerButton
                                                onCopy={() => handleCopy("busInzet.transvision")}
                                                data-testid="kopieer-alternatief-vervoer-transvision"
                                            />
                                        )
                                    }
                                />
                                <EvaluatiePunt
                                    form={form}
                                    name="busInzet.deWit"
                                    label="De Wit"
                                    placeholder="Beschrijf de businzet"
                                    defaultOpen={!!evaluatie?.busInzet?.deWit}
                                    onClear={handleChange}
                                    className="gap-0"
                                    readonly={isArchived}
                                    action={
                                        !isArchived && (
                                            <KopieerAlternatiefVervoerButton
                                                onCopy={() => handleCopy("busInzet.deWit")}
                                                data-testid="kopieer-alternatief-vervoer-de-wit"
                                            />
                                        )
                                    }
                                />
                            </div>
                        </EvaluatieSectie>
                    </Separated>
                </fieldset>
            </form>
            {putEvaluatie.error && <ErrorDisplay error={putEvaluatie.error} />}
            {validationError && <ErrorDisplay error={validationError} />}
        </Form>
    );
}

type KopieerAlternatiefVervoerButtonProps = {
    onCopy: () => void;
    "data-testid"?: string;
};

function KopieerAlternatiefVervoerButton({ onCopy, "data-testid": dataTestid }: KopieerAlternatiefVervoerButtonProps) {
    return (
        <Button
            type="button"
            icon="copy"
            variant="tertiary"
            iconStrokeWidth="thick"
            size="s"
            className="mb-2"
            onClick={onCopy}
            data-testid={dataTestid}
        >
            Kopieer gegevens uit 'Alternatief vervoer'
        </Button>
    );
}
